import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startGetUnifiedCampaignStatistics } from "../../../actions/adsCampaigns";
import { startTrackHomeFeatureUsage } from "../../../actions/homeFeatureActivity";
import { HOME_FEATURE_ID } from "../../../data/homeFeatures";
import {
  GLOBAL_REPORT_ADS_STATS_BREAKDOWNS,
  GLOBAL_REPORT_ADS_STATS_LEVELS,
  GLOBAL_REPORT_STATS_BREAKDOWNS,
  GLOBAL_REPORT_STATS_LEVELS,
  LEGACY_CAMPAIGNS_STATS_BREAKDOWNS,
  LEGACY_CAMPAIGNS_STATS_LEVELS,
} from "../../../utils/unifiedCampaignStatisticsAdapters";
import { ADS_CAMPAIGN_STATUS_DRAFT } from "../../../utils/constants";
import { aggregateCampaignStatisticsTotals } from "../utils/aggregateCampaignStatisticsTotals";
import { filterCampaignsByReportActivity } from "../utils/filterCampaignsByReportActivity";
import { getPreviousDateRange } from "../utils/getPreviousDateRange";

const GLOBAL_REPORT_STATS_CHUNK_SIZE = 25;
const GLOBAL_REPORT_STATS_CONCURRENCY = 2;

const generateCacheKey = (startDateTime, endDateTime) => {
  if (!startDateTime && !endDateTime) {
    return "no-filter";
  }
  return `${startDateTime || "null"}-${endDateTime || "null"}`;
};

const getNonDraftCampaignIDs = (campaigns = {}) =>
  Object.keys(campaigns).filter(
    (campaignID) =>
      campaigns[campaignID]?.states?.general !== ADS_CAMPAIGN_STATUS_DRAFT
  );

const chunkArray = (items = [], size = GLOBAL_REPORT_STATS_CHUNK_SIZE) => {
  if (!items.length) {
    return [];
  }
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const runWithConcurrency = async (items, concurrency, worker) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const runWorker = async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await worker(items[currentIndex], currentIndex);
    }
  };

  const poolSize = Math.min(concurrency, Math.max(items.length, 1));
  await Promise.all(Array.from({ length: poolSize }, () => runWorker()));
  return results;
};

const hasUnifiedDailySeries = (unifiedData, campaignIDs) => {
  const campaignsMap = unifiedData?.campaigns || {};

  return campaignIDs.some((campaignID) => {
    const unifiedCampaign = campaignsMap[campaignID];
    if (!unifiedCampaign) {
      return false;
    }

    return (unifiedCampaign.platforms || []).some((platform) => {
      const daily =
        unifiedCampaign?.levels?.campaign?.daily?.[platform]?.campaign;
      return Array.isArray(daily) && daily.length > 0;
    });
  });
};

const campaignHasAdsMaximumNode = (unifiedCampaign) => {
  if (!unifiedCampaign) {
    return false;
  }

  const platforms = unifiedCampaign.platforms || [];
  if (!platforms.length) {
    return true;
  }

  return Object.prototype.hasOwnProperty.call(
    unifiedCampaign?.levels?.ads || {},
    "maximum"
  );
};

const mergeUnifiedStatistics = (existing, incoming) => ({
  ...existing,
  ...incoming,
  campaigns: {
    ...existing?.campaigns,
    ...incoming?.campaigns,
  },
});

/** Merge ads-only responses without wiping campaign maximum/daily nodes. */
const mergeAdsStatisticsIntoCache = (existing, adsIncoming) => {
  const nextCampaigns = { ...(existing?.campaigns || {}) };

  Object.entries(adsIncoming?.campaigns || {}).forEach(
    ([campaignID, adsCampaign]) => {
      const current = nextCampaigns[campaignID];
      if (!current) {
        nextCampaigns[campaignID] = adsCampaign;
        return;
      }

      nextCampaigns[campaignID] = {
        ...current,
        platforms: [
          ...new Set([
            ...(current.platforms || []),
            ...(adsCampaign.platforms || []),
          ]),
        ],
        levels: {
          ...current.levels,
          ads: adsCampaign?.levels?.ads ?? current.levels?.ads,
        },
      };
    }
  );

  return {
    ...existing,
    campaigns: nextCampaigns,
  };
};

const fetchUnifiedStatisticsInBatches = async ({
  dispatch,
  campaignIDs,
  levels,
  breakdowns,
  startDateTime,
  endDateTime,
}) => {
  const chunks = chunkArray(campaignIDs, GLOBAL_REPORT_STATS_CHUNK_SIZE);

  if (!chunks.length) {
    return { ok: true, data: { campaigns: {} } };
  }

  const chunkResponses = await runWithConcurrency(
    chunks,
    GLOBAL_REPORT_STATS_CONCURRENCY,
    (chunkIDs) =>
      dispatch(
        startGetUnifiedCampaignStatistics({
          campaignIDs: chunkIDs,
          levels,
          breakdowns,
          startDateTime,
          endDateTime,
          options: { convertCurrencies: true },
        })
      )
  );

  const anyOk = chunkResponses.some((response) => response?.ok);
  if (!anyOk) {
    return { ok: false, data: { campaigns: {} } };
  }

  const merged = chunkResponses.reduce(
    (acc, response) =>
      response?.ok ? mergeUnifiedStatistics(acc, response.data) : acc,
    { campaigns: {} }
  );

  return { ok: true, data: merged };
};

/**
 * Fetches and aggregates campaign statistics for Global Report.
 * Candidates come from RTDB; final set is post-filtered by activity in range.
 * Ads maximum and previous-period stats load only for campaigns with activity.
 */
const useGlobalReportStatistics = ({
  campaigns = {},
  dateRange,
  isLoadingCampaigns,
}) => {
  const dispatch = useDispatchApp();
  const [statisticsCache, setStatisticsCache] = useState({});
  const [previousPeriodCache, setPreviousPeriodCache] = useState({});
  const [statisticsLoading, setStatisticsLoading] = useState([]);
  const [isLoadingCurrentStatistics, setIsLoadingCurrentStatistics] =
    useState(false);
  const [isLoadingPreviousPeriod, setIsLoadingPreviousPeriod] = useState(false);
  const [isLoadingAdsStatistics, setIsLoadingAdsStatistics] = useState(false);
  const [settledStatisticsCacheKey, setSettledStatisticsCacheKey] =
    useState(null);
  const [settledPreviousPeriodCacheKey, setSettledPreviousPeriodCacheKey] =
    useState(null);
  const [settledAdsStatisticsCacheKey, setSettledAdsStatisticsCacheKey] =
    useState(null);
  const currentRequestEpochRef = useRef(0);
  const previousRequestEpochRef = useRef(0);
  const adsRequestEpochRef = useRef(0);
  /** Avoid refetch loops when APIs omit nodes for some campaigns */
  const previousFetchedIDsRef = useRef({});
  const adsFetchedIDsRef = useRef({});

  const hasExplicitRange = !!(
    dateRange?.startDateTime && dateRange?.endDateTime
  );

  const cacheKey = generateCacheKey(
    dateRange?.startDateTime,
    dateRange?.endDateTime
  );

  const statistics = useMemo(
    () => statisticsCache[cacheKey] || {},
    [statisticsCache, cacheKey]
  );
  const previousPeriodStatistics = useMemo(
    () => previousPeriodCache[cacheKey] || {},
    [previousPeriodCache, cacheKey]
  );

  const isStatisticsSettledForRange =
    !!cacheKey && settledStatisticsCacheKey === cacheKey;

  const { campaignsWithActivity, incompleteCampaignIDs } = useMemo(() => {
    if (!isStatisticsSettledForRange) {
      return { campaignsWithActivity: {}, incompleteCampaignIDs: [] };
    }

    return filterCampaignsByReportActivity({
      campaigns,
      statistics,
    });
  }, [campaigns, isStatisticsSettledForRange, statistics]);

  const activeCampaignIDs = useMemo(
    () => Object.keys(campaignsWithActivity || {}),
    [campaignsWithActivity]
  );

  const hasIncompleteStatistics = incompleteCampaignIDs.length > 0;

  const previousDateRange = useMemo(() => {
    if (!hasExplicitRange) {
      return null;
    }
    return getPreviousDateRange({
      startDateTime: dateRange.startDateTime,
      endDateTime: dateRange.endDateTime,
    });
  }, [dateRange?.endDateTime, dateRange?.startDateTime, hasExplicitRange]);

  const needsPreviousPeriod = !!previousDateRange;

  const isPreviousPeriodSettledForRange =
    !needsPreviousPeriod ||
    (!!cacheKey && settledPreviousPeriodCacheKey === cacheKey);

  const isAdsStatisticsSettledForRange =
    !!cacheKey && settledAdsStatisticsCacheKey === cacheKey;

  const isLoadingStatistics =
    isLoadingCurrentStatistics ||
    (isStatisticsSettledForRange &&
      needsPreviousPeriod &&
      !isPreviousPeriodSettledForRange) ||
    isLoadingPreviousPeriod;

  // Current period: campaign maximum + daily for all candidates
  useEffect(() => {
    if (!hasExplicitRange || isLoadingCampaigns) {
      return;
    }

    const campaignIDs = getNonDraftCampaignIDs(campaigns);

    if (campaignIDs.length === 0) {
      setIsLoadingCurrentStatistics(false);
      setStatisticsLoading([]);
      setSettledStatisticsCacheKey(cacheKey);
      return;
    }

    const currentStats = statisticsCache[cacheKey] || {};
    const campaignsMap = currentStats.campaigns || {};
    const hasDailySeries = hasUnifiedDailySeries(currentStats, campaignIDs);
    const missingCampaignIDs = campaignIDs.filter(
      (campaignID) => !campaignsMap[campaignID]
    );

    if (missingCampaignIDs.length === 0 && hasDailySeries) {
      setIsLoadingCurrentStatistics(false);
      setSettledStatisticsCacheKey(cacheKey);
      return;
    }

    const requestEpoch = ++currentRequestEpochRef.current;
    setIsLoadingCurrentStatistics(true);
    setStatisticsLoading((prev) => [
      ...new Set([...prev, ...missingCampaignIDs]),
    ]);

    const fetchCampaignIDs =
      missingCampaignIDs.length > 0 ? missingCampaignIDs : campaignIDs;

    const loadStatistics = async () => {
      const currentResponse = await fetchUnifiedStatisticsInBatches({
        dispatch,
        campaignIDs: fetchCampaignIDs,
        levels: GLOBAL_REPORT_STATS_LEVELS,
        breakdowns: GLOBAL_REPORT_STATS_BREAKDOWNS,
        startDateTime: dateRange.startDateTime,
        endDateTime: dateRange.endDateTime,
      });

      let mergedCurrent = currentResponse?.ok
        ? currentResponse.data
        : { campaigns: {} };

      const stillMissing = fetchCampaignIDs.filter(
        (campaignID) => !mergedCurrent?.campaigns?.[campaignID]
      );

      if (stillMissing.length > 0) {
        const retryResponse = await fetchUnifiedStatisticsInBatches({
          dispatch,
          campaignIDs: stillMissing,
          levels: GLOBAL_REPORT_STATS_LEVELS,
          breakdowns: GLOBAL_REPORT_STATS_BREAKDOWNS,
          startDateTime: dateRange.startDateTime,
          endDateTime: dateRange.endDateTime,
        });

        if (retryResponse?.ok) {
          mergedCurrent = mergeUnifiedStatistics(
            mergedCurrent,
            retryResponse.data
          );
        }
      }

      return {
        mergedCurrent,
        currentOk: !!(
          currentResponse?.ok ||
          Object.keys(mergedCurrent?.campaigns || {}).length
        ),
      };
    };

    loadStatistics()
      .then(({ mergedCurrent, currentOk }) => {
        if (requestEpoch !== currentRequestEpochRef.current) {
          return;
        }

        if (currentOk || Object.keys(mergedCurrent?.campaigns || {}).length) {
          setStatisticsCache((prev) => ({
            ...prev,
            [cacheKey]: mergeUnifiedStatistics(prev[cacheKey], mergedCurrent),
          }));
          dispatch(
            startTrackHomeFeatureUsage({ featureID: HOME_FEATURE_ID.REPORTS })
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching global report statistics:", error);
      })
      .finally(() => {
        if (requestEpoch !== currentRequestEpochRef.current) {
          return;
        }

        setStatisticsLoading((prev) =>
          prev.filter((id) => !missingCampaignIDs.includes(id))
        );
        setIsLoadingCurrentStatistics(false);
        setSettledStatisticsCacheKey(cacheKey);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cache read inside effect; avoid refetch loops
  }, [
    cacheKey,
    campaigns,
    dateRange?.endDateTime,
    dateRange?.startDateTime,
    dispatch,
    hasExplicitRange,
    isLoadingCampaigns,
  ]);

  // Previous period: campaign maximum only, after activity filter
  useEffect(() => {
    if (!hasExplicitRange || isLoadingCampaigns) {
      return;
    }

    if (!isStatisticsSettledForRange) {
      setIsLoadingPreviousPeriod(false);
      return;
    }

    if (!needsPreviousPeriod) {
      setIsLoadingPreviousPeriod(false);
      setSettledPreviousPeriodCacheKey(cacheKey);
      return;
    }

    if (activeCampaignIDs.length === 0) {
      setIsLoadingPreviousPeriod(false);
      setSettledPreviousPeriodCacheKey(cacheKey);
      return;
    }

    const fetchedIDs = previousFetchedIDsRef.current[cacheKey] || new Set();
    const cachedPrevious = previousPeriodCache[cacheKey] || {};
    const previousCampaignsMap = cachedPrevious.campaigns || {};
    const missingCampaignIDs = activeCampaignIDs.filter(
      (campaignID) =>
        !fetchedIDs.has(campaignID) && !previousCampaignsMap[campaignID]
    );

    if (missingCampaignIDs.length === 0) {
      setIsLoadingPreviousPeriod(false);
      setSettledPreviousPeriodCacheKey(cacheKey);
      return;
    }

    const requestEpoch = ++previousRequestEpochRef.current;
    setIsLoadingPreviousPeriod(true);
    setSettledPreviousPeriodCacheKey(null);

    fetchUnifiedStatisticsInBatches({
      dispatch,
      campaignIDs: missingCampaignIDs,
      levels: LEGACY_CAMPAIGNS_STATS_LEVELS,
      breakdowns: LEGACY_CAMPAIGNS_STATS_BREAKDOWNS,
      startDateTime: previousDateRange.startDateTime,
      endDateTime: previousDateRange.endDateTime,
    })
      .then((previousResponse) => {
        if (requestEpoch !== previousRequestEpochRef.current) {
          return;
        }

        if (previousResponse?.ok) {
          setPreviousPeriodCache((prev) => ({
            ...prev,
            [cacheKey]: mergeUnifiedStatistics(
              prev[cacheKey],
              previousResponse.data
            ),
          }));
        }
      })
      .catch((error) => {
        console.error(
          "Error fetching global report previous-period statistics:",
          error
        );
      })
      .finally(() => {
        if (requestEpoch !== previousRequestEpochRef.current) {
          return;
        }

        const nextFetched = new Set([
          ...(previousFetchedIDsRef.current[cacheKey] || []),
          ...missingCampaignIDs,
        ]);
        previousFetchedIDsRef.current[cacheKey] = nextFetched;

        setIsLoadingPreviousPeriod(false);
        setSettledPreviousPeriodCacheKey(cacheKey);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cache read inside effect; avoid refetch loops
  }, [
    activeCampaignIDs,
    cacheKey,
    dispatch,
    hasExplicitRange,
    isLoadingCampaigns,
    isStatisticsSettledForRange,
    needsPreviousPeriod,
    previousDateRange?.endDateTime,
    previousDateRange?.startDateTime,
  ]);

  // Ads maximum: only for campaigns with activity (creatives)
  useEffect(() => {
    if (!hasExplicitRange || isLoadingCampaigns) {
      return;
    }

    if (!isStatisticsSettledForRange) {
      setIsLoadingAdsStatistics(false);
      return;
    }

    if (activeCampaignIDs.length === 0) {
      setIsLoadingAdsStatistics(false);
      setSettledAdsStatisticsCacheKey(cacheKey);
      return;
    }

    const fetchedIDs = adsFetchedIDsRef.current[cacheKey] || new Set();
    const currentStats = statisticsCache[cacheKey] || {};
    const missingAdsCampaignIDs = activeCampaignIDs.filter((campaignID) => {
      if (fetchedIDs.has(campaignID)) {
        return false;
      }
      return !campaignHasAdsMaximumNode(currentStats.campaigns?.[campaignID]);
    });

    if (missingAdsCampaignIDs.length === 0) {
      setIsLoadingAdsStatistics(false);
      setSettledAdsStatisticsCacheKey(cacheKey);
      return;
    }

    const requestEpoch = ++adsRequestEpochRef.current;
    setIsLoadingAdsStatistics(true);
    setSettledAdsStatisticsCacheKey(null);

    fetchUnifiedStatisticsInBatches({
      dispatch,
      campaignIDs: missingAdsCampaignIDs,
      levels: GLOBAL_REPORT_ADS_STATS_LEVELS,
      breakdowns: GLOBAL_REPORT_ADS_STATS_BREAKDOWNS,
      startDateTime: dateRange.startDateTime,
      endDateTime: dateRange.endDateTime,
    })
      .then((adsResponse) => {
        if (requestEpoch !== adsRequestEpochRef.current) {
          return;
        }

        if (adsResponse?.ok) {
          setStatisticsCache((prev) => ({
            ...prev,
            [cacheKey]: mergeAdsStatisticsIntoCache(
              prev[cacheKey],
              adsResponse.data
            ),
          }));
        }
      })
      .catch((error) => {
        console.error("Error fetching global report ads statistics:", error);
      })
      .finally(() => {
        if (requestEpoch !== adsRequestEpochRef.current) {
          return;
        }

        const nextFetched = new Set([
          ...(adsFetchedIDsRef.current[cacheKey] || []),
          ...missingAdsCampaignIDs,
        ]);
        adsFetchedIDsRef.current[cacheKey] = nextFetched;

        setIsLoadingAdsStatistics(false);
        setSettledAdsStatisticsCacheKey(cacheKey);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cache read inside effect; avoid refetch loops
  }, [
    activeCampaignIDs,
    cacheKey,
    dateRange?.endDateTime,
    dateRange?.startDateTime,
    dispatch,
    hasExplicitRange,
    isLoadingCampaigns,
    isStatisticsSettledForRange,
  ]);

  useEffect(() => {
    if (!hasExplicitRange) {
      setStatisticsLoading([]);
      setIsLoadingCurrentStatistics(false);
      setIsLoadingPreviousPeriod(false);
      setIsLoadingAdsStatistics(false);
      setSettledStatisticsCacheKey(null);
      setSettledPreviousPeriodCacheKey(null);
      setSettledAdsStatisticsCacheKey(null);
    }
  }, [cacheKey, hasExplicitRange]);

  const totals = useMemo(
    () =>
      aggregateCampaignStatisticsTotals({
        campaigns: campaignsWithActivity,
        statistics,
        dispatch,
      }),
    [campaignsWithActivity, dispatch, statistics]
  );

  const totalsLastPeriod = useMemo(() => {
    const previousCampaigns = previousPeriodStatistics?.campaigns || {};

    if (!hasExplicitRange || !Object.keys(previousCampaigns).length) {
      return null;
    }

    return aggregateCampaignStatisticsTotals({
      campaigns: campaignsWithActivity,
      statistics: previousPeriodStatistics,
      dispatch,
    });
  }, [
    campaignsWithActivity,
    dispatch,
    hasExplicitRange,
    previousPeriodStatistics,
  ]);

  return {
    statistics,
    previousPeriodStatistics,
    statisticsLoading,
    isLoadingStatistics,
    isLoadingCurrentStatistics,
    isLoadingPreviousPeriod,
    isLoadingAdsStatistics,
    isStatisticsSettledForRange,
    isPreviousPeriodSettledForRange,
    isAdsStatisticsSettledForRange,
    campaignsWithActivity,
    hasIncompleteStatistics,
    incompleteCampaignIDs,
    totals,
    totalsLastPeriod,
    hasActiveDateRange: hasExplicitRange,
  };
};

export default useGlobalReportStatistics;
